<script setup>
import { computed } from 'vue';
import SzSenderBadge from 'dashboard/components/widgets/conversation/solarz/SzSenderBadge.vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const { variant, orientation, inReplyTo, shouldGroupWithNext, sender, messageType, isPrivate, content, contentAttributes } =
  useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'sz-private-bubble [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'sz-failed-bubble',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return content;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <span class="break-all line-clamp-2">
        {{ replyToPreview }}
      </span>
    </div>
    <SzSenderBadge
      :sender="sender"
      :message-type="messageType"
      :is-private="isPrivate"
      :content="content"
      :content-attributes="contentAttributes"
    />
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>

<style>
/* SZ: estilo da bolha de mensagem com falha de envio (Figma 2026-05-26) */
#app .sz-failed-bubble {
  background: #FCD9D9 !important;
  color: #191919 !important;
  width: 322px !important;
  max-width: 322px !important;
  padding: 8px 12px 4px 8px !important;
  gap: 4px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-end !important;
  justify-content: center !important;
  border-radius: 8px 0 8px 8px !important;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.16) !important;
}

#app .sz-failed-bubble .sz-sender-badge,
#app .sz-failed-bubble [data-sz-sender-badge] {
  background: #E6ADAD !important;
  border-radius: 999px !important;
  padding: 2px 8px 2px 2px !important;
  gap: 4px !important;
  display: inline-flex !important;
  align-items: center !important;
  align-self: flex-start !important;
  width: auto !important;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  color: #191919 !important;
}
#app .sz-failed-bubble .sz-sender-badge *,
#app .sz-failed-bubble [data-sz-sender-badge] * {
  color: #191919 !important;
}

#app .sz-failed-bubble > *:not(.sz-sender-badge):not([data-sz-sender-badge]):not(.message-meta) {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #191919;
  align-self: stretch;
}

#app .sz-failed-bubble .message-meta {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;
  align-items: center !important;
  gap: 8px !important;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400 !important;
  font-size: 12px !important;
  line-height: 20px !important;
  color: #000000 !important;
  margin-top: 4px !important;
}
#app .sz-failed-bubble .message-meta * {
  color: #000000 !important;
}
#app .sz-failed-bubble .message-meta svg {
  width: 16px !important;
  height: 16px !important;
  color: #6B2A2A !important;
}
#app .sz-failed-bubble .message-meta svg path,
#app .sz-failed-bubble .message-meta svg circle {
  stroke: #6B2A2A !important;
}

.sz-failed-side-badge {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #F2B9B9;
  border-radius: 999px;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.1);
  margin-left: 8px;
  flex-shrink: 0;
  align-self: center;
}
.sz-failed-side-badge svg {
  width: 16px;
  height: 16px;
}
.sz-failed-side-badge svg path,
.sz-failed-side-badge svg line {
  stroke: #6B2A2A;
}

.dark #app .sz-failed-bubble {
  background: #5D2C2C !important;
  color: #FFFFFF !important;
}
.dark #app .sz-failed-bubble .sz-sender-badge,
.dark #app .sz-failed-bubble [data-sz-sender-badge] {
  background: #3F1D1D !important;
  color: #FFFFFF !important;
}
.dark #app .sz-failed-bubble .sz-sender-badge *,
.dark #app .sz-failed-bubble [data-sz-sender-badge] * {
  color: #FFFFFF !important;
}
.dark #app .sz-failed-bubble > *:not(.sz-sender-badge):not([data-sz-sender-badge]):not(.message-meta) {
  color: #FFFFFF;
}
.dark #app .sz-failed-bubble .message-meta,
.dark #app .sz-failed-bubble .message-meta * {
  color: rgba(255, 255, 255, 0.64) !important;
}
.dark #app .sz-failed-bubble .message-meta svg {
  color: #EB9696 !important;
}
.dark #app .sz-failed-bubble .message-meta svg path,
.dark #app .sz-failed-bubble .message-meta svg circle {
  stroke: #EB9696 !important;
}
.dark .sz-failed-side-badge {
  background: #5D2C2C;
}
.dark .sz-failed-side-badge svg path,
.dark .sz-failed-side-badge svg line {
  stroke: #D88888;
}

#app .sz-failed-bubble.left-bubble,
#app .sz-failed-bubble.right-bubble {
  border-bottom-left-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
  border-top-left-radius: 8px !important;
  border-top-right-radius: 0 !important;
}

/* SZ: estilo da bolha de nota privada (Figma 2026-05-26) */
#app .sz-private-bubble {
  background: #FEF3C6 !important;
  color: #352A05 !important;
  width: 366px !important;
  max-width: 366px !important;
  padding: 8px 12px 4px 8px !important;
  gap: 4px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-end !important;
  justify-content: center !important;
  border-radius: 8px 0 8px 8px !important;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.16) !important;
}
#app .sz-private-bubble.left-bubble,
#app .sz-private-bubble.right-bubble {
  border-bottom-left-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
  border-top-left-radius: 8px !important;
  border-top-right-radius: 0 !important;
}

/* Sender pill amarelo */
#app .sz-private-bubble .sz-sender-badge,
#app .sz-private-bubble [data-sz-sender-badge] {
  background: #EBDA98 !important;
  border-radius: 999px !important;
  padding: 2px 8px 2px 2px !important;
  gap: 4px !important;
  display: inline-flex !important;
  align-items: center !important;
  align-self: flex-start !important;
  width: auto !important;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  color: #191919 !important;
}
#app .sz-private-bubble .sz-sender-badge *,
#app .sz-private-bubble [data-sz-sender-badge] * {
  color: #191919 !important;
}

/* Conteudo do body */
#app .sz-private-bubble > *:not(.sz-sender-badge):not([data-sz-sender-badge]):not(.message-meta) {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #352A05;
  align-self: stretch;
}

/* Meta: disclaimer (esquerda) + timestamp (direita) */
#app .sz-private-bubble .message-meta {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 4px !important;
  margin-top: 4px !important;
  width: 100% !important;
  align-self: stretch !important;
}
#app .sz-private-bubble .message-meta .sz-meta-disclaimer {
  flex-grow: 1;
  text-align: left;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 1 !important;
  color: #6F4500 !important;
}
#app .sz-private-bubble .message-meta time,
#app .sz-private-bubble .message-meta .inline {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400 !important;
  font-size: 12px !important;
  line-height: 20px !important;
  color: rgba(0, 0, 0, 0.8) !important;
}
/* Esconde lock icon e clock icon (Figma nao mostra na nota privada) */
#app .sz-private-bubble .message-meta .sz-meta-clock,
#app .sz-private-bubble .message-meta [class*="lock-keyhole"] {
  display: none !important;
}

/* Dark mode */
.dark #app .sz-private-bubble {
  background: #856811 !important;
  color: rgba(255, 255, 255, 0.8) !important;
  box-shadow: none !important;
}
.dark #app .sz-private-bubble .sz-sender-badge,
.dark #app .sz-private-bubble [data-sz-sender-badge] {
  background: #4D4018 !important;
  color: #FFFFFF !important;
}
.dark #app .sz-private-bubble .sz-sender-badge *,
.dark #app .sz-private-bubble [data-sz-sender-badge] * {
  color: #FFFFFF !important;
}
.dark #app .sz-private-bubble,
.dark #app .sz-private-bubble *:not(.sz-sender-badge):not([data-sz-sender-badge]):not(.sz-sender-badge *):not(.message-meta):not(.message-meta *) {
  color: rgba(255, 255, 255, 0.8) !important;
}
.dark #app .sz-private-bubble .message-meta .sz-meta-disclaimer {
  color: #1E1507 !important;
}
.dark #app .sz-private-bubble .message-meta time,
.dark #app .sz-private-bubble .message-meta .inline {
  color: #E0DAC6 !important;
}

</style>

